import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { Box, Button, LinearProgress } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { coursePackageApi } from '../../../../../../features/course/coursePackage'
import { CoursePackageState } from '../../../../../../features/types'
import BundleTable from '../components/BundleTable'
import BundleDialog from '../components/BundleDialog'
import Sentinel from '../../../../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../../../../app/hooks/usePaginatedList'

interface Props {
    coursePriceId: number
}

const styles = {
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
}

const BundleTab: React.FC<Props> = ({ coursePriceId }) => {
    const { formatMessage } = useIntl()
    const [openBundleDialog, setOpenBundleDialog] = useState(false)
    const [currentRowData, setCurrentRowData] =
        useState<CoursePackageState>(null)

    const [fetchPackages] =
        coursePackageApi.useLazyGetCoursePackagesByCoursePriceQuery()

    const {
        rows,
        isLoading: isFetching,
        loadedPages,
        sentinelRef,
        reload,
    } = usePaginatedList(
        (page, size) =>
            fetchPackages({ coursePriceId, page, size }).unwrap(),
        [coursePriceId],
        { skip: !coursePriceId },
    )

    const [deletePackage, { isLoading: isDeleting }] =
        coursePackageApi.useDeleteCoursePackageMutation()

    const isLoading = (isFetching && loadedPages === 0) || isDeleting

    const handleRowUpdate = (coursePackage: CoursePackageState) => {
        setCurrentRowData(coursePackage)
        setOpenBundleDialog(true)
    }

    const handleDelete = async (id: number) => {
        try {
            await deletePackage(id).unwrap()
            toast.success(formatMessage({ id: 'deletedSuccess' }))
            reload()
        } catch (e) {}
    }

    useEffect(() => {
        if (!openBundleDialog) {
            setCurrentRowData(null)
        }
    }, [openBundleDialog])

    return (
        <Box sx={styles.flexColumn}>
            <Box
                ml="auto"
                mb={2}
            >
                <Button
                    onClick={() => setOpenBundleDialog(true)}
                    variant="contained"
                    endIcon={<AddIcon />}
                >
                    {formatMessage({ id: 'addNewPackage' })}
                </Button>
            </Box>

            {isLoading ? (
                <LinearProgress />
            ) : (
                <>
                    <BundleTable
                        packages={rows}
                        isFetching={isFetching}
                        onDelete={handleDelete}
                        onEdit={handleRowUpdate}
                    />

                    <Sentinel ref={sentinelRef} isLoading={isFetching} />
                </>
            )}

            <BundleDialog
                editPackage={currentRowData}
                coursePriceId={coursePriceId}
                open={openBundleDialog}
                handleClose={() => setOpenBundleDialog(false)}
            />
        </Box>
    )
}

export default BundleTab
